import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Brain,
  Target,
  Zap,
  BarChart3,
  Users,
  CheckCircle,
  ArrowRight,
  Clock,
  Star,
  Lightbulb,
} from "lucide-react";

const Features: React.FC = () => {
  const features = [
    {
      icon: Target,
      title: "AI-Powered Candidate Matching",
      description:
        "Advanced algorithms match candidates to job roles with precision.",
      benefits: [
        "Precision matching algorithms",
        "Best fit for both parties",
        "Reduced hiring mistakes",
      ],
    },
    {
      icon: Brain,
      title: "Adaptive Testing & Assessments",
      description:
        "Customized tests that adapt to candidates' skills and roles.",
      benefits: [
        "Accurate skill evaluation",
        "Role-specific assessments",
        "Adaptive difficulty levels",
      ],
    },
    {
      icon: Zap,
      title: "Automated Screening Workflows",
      description:
        "Saves recruiter time by automating resume parsing and shortlisting.",
      benefits: [
        "Automated resume parsing",
        "Intelligent shortlisting",
        "Time-saving workflows",
      ],
    },
    {
      icon: Users,
      title: "Seamless Integrations",
      description: "Works with existing ATS, CRMs, and communication tools.",
      benefits: ["ATS integration", "CRM compatibility", "Smooth workflows"],
    },
    {
      icon: BarChart3,
      title: "Analytics Dashboard",
      description:
        "Real-time insights into hiring patterns, bottlenecks, and outcomes.",
      benefits: [
        "Real-time insights",
        "Pattern recognition",
        "Performance optimization",
      ],
    },
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "HR Director at TechCorp",
      quote:
        "VetriaAI's AI-powered testing has revolutionized our hiring process. We've reduced screening time by 80% while improving candidate quality significantly.",
      avatar:
        "https://images.pexels.com/photos/774909/pexels-photo-774909.jpeg?auto=compress&cs=tinysrgb&w=150",
    },
    {
      name: "Michael Chen",
      role: "Software Engineer",
      quote:
        "The assessment process was fair and comprehensive. I loved how the AI interview adapted to my responses and provided immediate feedback.",
      avatar:
        "https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=150",
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="/vetriaAI.png"
                alt="VetriaAI Logo"
                className="w-10 h-10"
              />
              <span className="text-2xl font-bold text-gray-900">VetriaAI</span>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            <Link
              to="/login"
              className="text-gray-600 hover:text-gray-900 font-medium"
            >
              Sign In
            </Link>
            <Link
              to="/login"
              className="bg-red-800 text-white px-6 py-2 rounded-lg hover:bg-red-900 transition-colors font-medium"
            >
              Sign In
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-red-50 to-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">
            Why VetriaAI?
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Traditional hiring is slow, biased, and inefficient. VetriaAI
            changes that with a smarter, faster, and fairer approach.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="bg-red-800 text-white px-8 py-4 rounded-lg hover:bg-red-900 transition-colors font-semibold text-lg inline-flex items-center justify-center"
            >
              Sign In <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link
              to="/"
              className="border border-gray-300 text-gray-700 px-8 py-4 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-lg"
            >
              View Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Key Features
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Powerful tools designed to transform your hiring process
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-shadow border border-gray-100"
                >
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-red-800" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {feature.description}
                  </p>
                  <ul className="space-y-2">
                    {feature.benefits.map((benefit, idx) => (
                      <li
                        key={idx}
                        className="flex items-center text-sm text-gray-600"
                      >
                        <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-gray-100 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Use Cases</h2>
            <p className="text-xl text-gray-600">
              VetriaAI adapts to your specific hiring needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                High-volume hiring
              </h3>
              <p className="text-gray-600 mb-6">
                Perfect for IT, retail, BPO, and healthcare industries requiring
                rapid talent acquisition.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Campus recruitment campaigns
              </h3>
              <p className="text-gray-600 mb-6">
                Streamline university partnerships and graduate hiring programs.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Diversity & inclusion programs
              </h3>
              <p className="text-gray-600 mb-6">
                Remove bias and promote inclusive hiring practices.
              </p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Niche technical roles
              </h3>
              <p className="text-gray-600 mb-6">
                Find rare talent for specialized positions requiring specific
                skills.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              How VetriaAI Works
            </h2>
            <p className="text-xl text-gray-600">
              Simple, efficient, and powerful - see how easy it is to get
              started
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="w-8 h-8 text-red-800" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                1. Upload Job Description
              </h3>
              <p className="text-gray-600">
                Simply upload your job description or requirements, and our AI
                will analyze the key skills needed.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Brain className="w-8 h-8 text-red-800" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                2. AI Generates Tests
              </h3>
              <p className="text-gray-600">
                Our AI automatically creates customized assessments tailored to
                your specific job requirements.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-red-800" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                3. Get Qualified Candidates
              </h3>
              <p className="text-gray-600">
                Review detailed results and insights to make informed hiring
                decisions with confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-800 px-6">
        <div className="max-w-4xl mx-auto text-center text-white">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Experience These Features?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of companies already using VetriaAI to find the best
            talent faster.
          </p>
          <Link
            to="/login"
            className="bg-white text-red-800 px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg inline-flex items-center"
          >
            Sign In <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img
                  src="/vetriaAI.png"
                  alt="VetriaAI Logo"
                  className="w-8 h-8"
                />
                <span className="text-xl font-bold">VetriaAI</span>
              </div>
              <p className="text-gray-400">
                Revolutionizing recruitment with AI-powered intelligence.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-900">Product</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/features" className="text-white hover:text-gray-300">
                    Features
                  </Link>
                </li>
                <li>
                  <Link to="/security" className="text-white hover:text-gray-300">
                    Security
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-900">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-white hover:text-gray-300">
                    About
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-white hover:text-gray-300">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-900">Support</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/help-center" className="text-white hover:text-gray-300">
                    Help Center
                  </Link>
                </li>
                <li>
                  <Link to="/status" className="text-white hover:text-gray-300">
                    Status
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-red-900">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/cancellations-refunds" className="text-white hover:text-gray-300">
                    Cancellations &amp; Refunds
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-white hover:text-gray-300">
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/shipping" className="text-white hover:text-gray-300">
                    Shipping (Digital)
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-white hover:text-gray-300">
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>2025 VetriaAI. All rights and trademarks are reserved by VetriaAI.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Features;
